import * as React from 'react';
import { authService, LoginCredentials, User, AuthError } from '../services/authService';
import { fcmService } from '../crud-dashboard/services/fcmService';
import { faceAuthService, FaceAuthStatus } from '../services/faceAuthService';
import { usePingPong } from '../crud-dashboard/hooks/usePingPong';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  faceAuthStatus: FaceAuthStatus | null;
  refreshFaceAuthStatus: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [faceAuthStatus, setFaceAuthStatus] = React.useState<FaceAuthStatus | null>(() => {
    // Load from localStorage on initialization
    const stored = localStorage.getItem('face_auth_status');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  // Load Face Auth status from API and store it
  const loadFaceAuthStatus = React.useCallback(async () => {
    try {
      const response = await faceAuthService.getStatus();
      if (response.success && response.data) {
        setFaceAuthStatus(response.data);
        // Store in localStorage
        localStorage.setItem('face_auth_status', JSON.stringify(response.data));
      } else {
        setFaceAuthStatus(null);
        localStorage.removeItem('face_auth_status');
      }
    } catch (error) {
      setFaceAuthStatus(null);
      localStorage.removeItem('face_auth_status');
    }
  }, []);

  // Check authentication status on mount
  React.useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      const storedUser = authService.getUser();

      if (token && storedUser) {
        setUser(storedUser);
        // Initialize FCM for already logged-in users
        await fcmService.initialize();
        // Only load Face Auth status if not already in localStorage
        const storedStatus = localStorage.getItem('face_auth_status');
        if (!storedStatus) {
          await loadFaceAuthStatus();
        }
      } else if (token) {
        // Token exists but no user data, try to fetch
        try {
          const userData = await authService.fetchAndStoreUser(token);
          setUser(userData);
          // Initialize FCM after fetching user data
          await fcmService.initialize();
          // Only load Face Auth status if not already in localStorage
          const storedStatus = localStorage.getItem('face_auth_status');
          if (!storedStatus) {
            await loadFaceAuthStatus();
          } else {
          }
        } catch (error) {
          // If fetching fails, clear auth
          authService.clearAuth();
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, [loadFaceAuthStatus]);

  // Listen for storage changes (e.g., when authService.clearAuth() is called)
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Check if auth token was removed
      if (e.key === 'auth_token' && e.newValue === null) {
        setUser(null);
      }
      // Check if user was updated
      else if (e.key === 'auth_user') {
        if (e.newValue === null) {
          setUser(null);
        } else {
          try {
            const updatedUser = JSON.parse(e.newValue);
            setUser(updatedUser);
          } catch {
            setUser(null);
          }
        }
      }
    };

    // Listen to storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Custom event for same-tab storage changes
    const handleCustomStorageChange = ((e: CustomEvent) => {
      handleStorageChange(e.detail);
    }) as EventListener;

    window.addEventListener('auth-storage-change', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-storage-change', handleCustomStorageChange);
    };
  }, []);

  const login = React.useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.login(credentials);
      const user = authService.getUser();
      setUser(user);
      // Initialize FCM after successful login
      await fcmService.initialize();
      // Load Face Auth status after login (always fetch on fresh login)
      await loadFaceAuthStatus();
    } catch (error) {
      if (error instanceof AuthError) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [loadFaceAuthStatus]);

  const logout = React.useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
      setFaceAuthStatus(null);
      // Clear stored Face Auth status
      localStorage.removeItem('face_auth_status');
      // Clear FCM token on logout
      fcmService.clearToken();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  usePingPong(!!user);

  const value = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      error,
      clearError,
      faceAuthStatus,
      refreshFaceAuthStatus: loadFaceAuthStatus,
    }),
    [user, isLoading, login, logout, error, clearError, faceAuthStatus, loadFaceAuthStatus]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

